import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Segmented,
  Space,
  Switch,
  Table,
  Tag,
  message,
} from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { adminApi, type PageResult } from '../../api'
import { HtmlDocEditor, type HtmlDocEditorHandle } from '../../components/HtmlDocEditor'
import { MarkdownDocEditor, type MarkdownDocEditorHandle } from '../../components/MarkdownDocEditor'
import { PageHeader, tablePagination, useServerPagination } from '../../components/ui'
import type { DocTemplateFormat, DocTemplateItem } from '../../types/docTemplate'
import './DocTemplateManage.css'

const FORMAT_OPTIONS: { value: DocTemplateFormat; label: string }[] = [
  { value: 'markdown', label: 'Markdown' },
  { value: 'html', label: '网页文档 (HTML)' },
]

const DEFAULT_CONTENT: Record<DocTemplateFormat, string> = {
  markdown: '# 文档标题\n\n## 第一节\n\n正文…',
  html: '<h2>文档标题</h2><p>正文…</p>',
}

export default function DocTemplateManage() {
  const { page, pageSize, handlePaginationChange, paginationOptions } = useServerPagination()
  const [formatFilter, setFormatFilter] = useState<DocTemplateFormat>('markdown')
  const [total, setTotal] = useState(0)
  const [rows, setRows] = useState<DocTemplateItem[]>([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<DocTemplateItem | null>(null)
  const [saving, setSaving] = useState(false)
  const [markdownDraft, setMarkdownDraft] = useState('')
  const [htmlDraft, setHtmlDraft] = useState('')
  const [form] = Form.useForm()
  const mdEditorRef = useRef<MarkdownDocEditorHandle>(null)
  const htmlEditorRef = useRef<HtmlDocEditorHandle>(null)

  const modalFormat = (Form.useWatch('doc_format', form) as DocTemplateFormat | undefined) ?? formatFilter

  const load = useCallback(
    (p: number, ps: number, fmt: DocTemplateFormat) => {
      setLoading(true)
      return adminApi
        .get<PageResult<DocTemplateItem>>('/admin/doc-templates', {
          params: { page: p, page_size: ps, doc_format: fmt },
        })
        .then((r) => {
          setRows(r.data.items)
          setTotal(r.data.total)
        })
        .catch((e) => message.error(e.message))
        .finally(() => setLoading(false))
    },
    [],
  )

  useEffect(() => {
    void load(page, pageSize, formatFilter)
  }, [page, pageSize, formatFilter, load])

  const flushMarkdownDraft = () => {
    const md = mdEditorRef.current?.getDocumentContent()
    if (md != null) setMarkdownDraft(md)
  }

  const handleCreateFormatChange = (nextFmt: DocTemplateFormat) => {
    if (editing || nextFmt === modalFormat) return
    if (modalFormat === 'markdown') flushMarkdownDraft()
  }

  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    form.setFieldsValue({
      doc_format: formatFilter,
      sort_no: 0,
      is_active: true,
    })
    setMarkdownDraft(DEFAULT_CONTENT.markdown)
    setHtmlDraft(DEFAULT_CONTENT.html)
    setOpen(true)
  }

  const openEdit = (row: DocTemplateItem) => {
    setEditing(row)
    form.setFieldsValue({
      title: row.title,
      description: row.description,
      doc_format: row.doc_format,
      sort_no: row.sort_no,
      is_active: row.is_active,
      template_code: row.template_code || undefined,
    })
    setMarkdownDraft(row.doc_format === 'markdown' ? row.content || DEFAULT_CONTENT.markdown : DEFAULT_CONTENT.markdown)
    setHtmlDraft(row.doc_format === 'html' ? row.content || DEFAULT_CONTENT.html : DEFAULT_CONTENT.html)
    setOpen(true)
  }

  const resolveBodyContent = () => {
    if (modalFormat === 'markdown') {
      return (mdEditorRef.current?.getDocumentContent() ?? markdownDraft).trim()
    }
    return htmlDraft.trim()
  }

  const save = async () => {
    const values = await form.validateFields()
    const content = resolveBodyContent()
    if (!content) {
      message.warning('请输入模板正文')
      return
    }
    setSaving(true)
    try {
      if (editing) {
        await adminApi.put(`/admin/doc-templates/${editing.id}`, {
          title: values.title,
          description: values.description,
          content,
          sort_no: values.sort_no,
          is_active: values.is_active,
          template_code: values.template_code,
        })
        message.success('模板已更新')
      } else {
        await adminApi.post('/admin/doc-templates', { ...values, content })
        message.success('模板已创建')
      }
      setOpen(false)
      await load(page, pageSize, formatFilter)
    } catch (e) {
      message.error((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  const remove = (row: DocTemplateItem) => {
    adminApi
      .delete(`/admin/doc-templates/${row.id}`)
      .then(() => {
        message.success('已删除')
        return load(page, pageSize, formatFilter)
      })
      .catch((e) => message.error(e.message))
  }

  const editorKey = editing?.id ?? 'new'

  return (
    <div>
      <PageHeader
        title="文档模板"
        description="维护新建文档时可选用的大纲模板。Markdown 与网页文档各维护一套，前台按编辑器类型展示。"
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Segmented
          value={formatFilter}
          options={FORMAT_OPTIONS}
          onChange={(v) => {
            setFormatFilter(v as DocTemplateFormat)
            handlePaginationChange(1, pageSize)
          }}
        />
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          新建模板
        </Button>
      </div>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={rows}
        pagination={tablePagination(page, total, handlePaginationChange, paginationOptions)}
        columns={[
          { title: '标题', dataIndex: 'title', ellipsis: true },
          { title: '说明', dataIndex: 'description', ellipsis: true },
          {
            title: '类型',
            dataIndex: 'doc_format',
            width: 120,
            render: (v: DocTemplateFormat) =>
              v === 'markdown' ? <Tag color="blue">Markdown</Tag> : <Tag color="green">HTML</Tag>,
          },
          { title: '排序', dataIndex: 'sort_no', width: 72 },
          {
            title: '状态',
            dataIndex: 'is_active',
            width: 88,
            render: (v: boolean) => (v ? <Tag color="success">启用</Tag> : <Tag>停用</Tag>),
          },
          {
            title: '来源',
            dataIndex: 'is_builtin',
            width: 88,
            render: (v: boolean) => (v ? <Tag>内置</Tag> : <Tag color="processing">自定义</Tag>),
          },
          {
            title: '操作',
            key: 'actions',
            width: 140,
            render: (_, row) => (
              <Space size={4}>
                <Button type="link" size="small" onClick={() => openEdit(row)}>
                  编辑
                </Button>
                {row.is_builtin ? null : (
                  <Popconfirm title="确定删除该模板？" onConfirm={() => remove(row)}>
                    <Button type="link" size="small" danger>
                      删除
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            ),
          },
        ]}
      />

      <Modal
        className="doc-template-manage-modal"
        title={editing ? '编辑模板' : '新建模板'}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => void save()}
        confirmLoading={saving}
        width={920}
        destroyOnHidden
        centered
      >
        <Form form={form} layout="vertical" className="doc-template-manage-meta">
          <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入标题' }]}>
            <Input maxLength={128} />
          </Form.Item>
          <Form.Item name="description" label="说明">
            <Input maxLength={512} />
          </Form.Item>
          <Form.Item name="doc_format" label="文档类型" rules={[{ required: true }]}>
            <Segmented
              options={FORMAT_OPTIONS}
              disabled={Boolean(editing)}
              onChange={(v) => handleCreateFormatChange(v as DocTemplateFormat)}
            />
          </Form.Item>
          <Form.Item name="sort_no" label="排序">
            <InputNumber min={0} max={9999} style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="is_active" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="template_code" label="模板编码">
            <Input maxLength={64} placeholder="可选，同类型唯一" disabled={editing?.is_builtin} />
          </Form.Item>
        </Form>

        <div className="doc-template-manage-editor-wrap">
          <div className="doc-template-manage-editor-label">模板正文</div>
          <div className="doc-template-manage-editor-host">
            {modalFormat === 'markdown' ? (
              <MarkdownDocEditor
                key={`md-${editorKey}`}
                ref={mdEditorRef}
                value={markdownDraft}
                onChange={setMarkdownDraft}
              />
            ) : (
              <HtmlDocEditor
                key={`html-${editorKey}`}
                ref={htmlEditorRef}
                value={htmlDraft}
                onChange={setHtmlDraft}
                placeholder="输入网页文档模板正文…"
              />
            )}
          </div>
        </div>
      </Modal>
    </div>
  )
}
